import PropTypes from 'prop-types';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { TrlTextField } from '»components/atoms/Inputs';
import {
  useCurrentPage,
  useHandleSagaError,
  useSendOtpBtn,
} from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';
import { getOperationLabel } from '»utils/trading/trading.utils';

import { isAuthenticatedSelector } from '../redux';
import { otpService } from './otp.services';
import { OtpContainer, SendCodeButton } from './Otp.styles';

export const Otp = ({
  margin,
  value,
  onChangeValue,
  onError,
  textFieldName,
  token,
  ordType,
  symbol,
  orderQty,
  price,
  amount,
  currency,
  currencySymbol,
  isTighter,
  isDisabled,
  username,
  currentRoute,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const { isOnboardingPage } = useCurrentPage(currentRoute);
  const { handleSagaError } = useHandleSagaError();
  const {
    isSendOtpBtnOnHold,
    disableSendOtpButton,
    enableSendOtpButtonWithDelay,
  } = useSendOtpBtn();

  const ordTypeExpression = ordType && getOperationLabel(ordType);

  const ordTypeLabel =
    ordTypeExpression && TrlFormattedMessagePlain({ id: ordTypeExpression });

  const getOTP = async () => {
    setIsLoading(true);
    disableSendOtpButton();
    enableSendOtpButtonWithDelay();

    if (token !== undefined) {
      otpService
        .getOTP({ token })
        .then(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          handleSagaError(error);
          const [{ detail }] = error;
          onError(detail);
          setIsLoading(false);
        });
    } else if (!isAuthenticated) {
      otpService
        .getOTPByUser({ username })
        .then(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          const [{ detail }] = error;
          onError(detail);
          setIsLoading(false);
        });
    } else {
      otpService
        .getOTPAuth({
          ordType: ordTypeLabel,
          symbol,
          orderQty,
          price,
          amount,
          currency,
          currencySymbol,
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          handleSagaError(error);
          const [{ detail }] = error;
          onError(detail);
          setIsLoading(false);
        });
    }
  };

  const fieldLabelExp = isOnboardingPage
    ? 'TRLT_UI-VALIDATION_CODE'
    : 'TRLT_UI-INSERT_CODE';

  return (
    <OtpContainer id="divSMSTokenAuth" margin={margin} isTighter={isTighter}>
      {textFieldName ? (
        <TrlTextField
          id="otpInput"
          name={textFieldName}
          type="text"
          label={<FormattedMessage id={fieldLabelExp} />}
          disabled={isDisabled}
        />
      ) : (
        <TrlTextField
          id="otpInput"
          name="inputSMSToken"
          type="text"
          label={<FormattedMessage id={fieldLabelExp} />}
          value={value}
          additionalFunc={onChangeValue}
          disabled={isDisabled}
        />
      )}
      {!isOnboardingPage && (
        <SendCodeButton
          id="sendCodeButton"
          textId={'TRLT_UI-SEND_CODE_METHOD'}
          isLoading={isLoading}
          disabled={isLoading || isSendOtpBtnOnHold || isDisabled}
          variant="main"
          onClick={getOTP}
        />
      )}
    </OtpContainer>
  );
};

Otp.propTypes = {
  margin: PropTypes.string,
  value: PropTypes.string,
  onChangeValue: PropTypes.func,
  onError: PropTypes.func,
  textFieldName: PropTypes.string,
  token: PropTypes.string,
  ordType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  symbol: PropTypes.string,
  orderQty: PropTypes.string,
  price: PropTypes.string,
  amount: PropTypes.number,
  currency: PropTypes.string,
  currencySymbol: PropTypes.string,
  isTighter: PropTypes.bool,
  isDisabled: PropTypes.bool,
  username: PropTypes.string,
  currentRoute: PropTypes.string,
};
